import { defineStore } from 'pinia';
import { ref } from 'vue';
import mixpanel from 'mixpanel-browser';
import { capitalize } from 'lodash-es';
import { intervalToDuration } from 'date-fns';
import { isNotTestAccount } from '@/helpers/thirdParty/verifyTestAccount';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';

export const useTracking = defineStore('tracking', () => {
  // state
  const sessionPage = ref(null);
  const sessionStart = ref(null);

  // actions

  /* ===========================
  General Tracking
  =========================== */

  const track = ({ name, params }) => {
    const {
      atsId, userId, integrationType, ipAddress, rbac,
    } = useAppAuthData().authData;
    const customParams = {
      ...params,
      'ATS Id': atsId,
      Integration: integrationType,
      role: rbac.roleNames.join(', '),
      'IP Address': ipAddress,
    };
    try {
    /* Don't track if user is engineeringuptimemetrics (AWS Canary tests that run every 5 mins) */
      if (window && isNotTestAccount(userId)) {
        if (import.meta.env.VUE_APP_MIXPANEL_LOG === 'true') {
          /* eslint-disable-next-line no-console */
          console.log('mixpanel', name, customParams);
        } else {
          mixpanel.track(name, customParams);
        }
      }
    // eslint-disable-next-line no-empty
    } catch (ignore) {}
  };

  const trackName = ({ name }) => track({ name });

  const trackJob = ({ name, job, props }) => {
    const {
      jobId, company, title, location, score, wordCount, revisionId, documentType,
    } = job;

    track({
      name,
      params: {
        Id: Number(jobId),
        Company: company,
        'Job Title': title,
        'Job Location': location,
        Score: score,
        RevisionId: revisionId,
        WordCount: wordCount,
        DocumentType: documentType,
        ...props,
      },
    });
  };

  const trackPageLoad = ({ page }) => trackName({ name: `Landed on Datapeople ${page} Page` });

  /* ===========================
  Session Duration
  =========================== */

  const handleSession = (page) => {
    if (sessionPage.value !== page) {
      if (!sessionStart.value) {
        sessionPage.value = page;
        sessionStart.value = Date.now();
      } else {
      /* If start already exists, trigger an end event, and set a new start */
        const end = Date.now();
        const { hours, minutes, seconds } = intervalToDuration({
          start: sessionStart.value,
          end,
        });
        track({
          name: `Session duration: ${sessionPage.value}`,
          params: { hours, minutes, seconds },
        });
        sessionPage.value = page;
        sessionStart.value = Date.now();
      }
    }
  };

  const onUpdateRoute = ({ route }) => {
    [
      'settings',
      'published',
      'unpublished',
      'flagship',
      'email',
      'draft',
      'reporting',
      'analytics',

    ].forEach((page) => {
      if (route.path.includes(page)) {
        handleSession(route.name);
      }
    });
  };

  useAppRoute().$subscribe((mutation, state) => {
    onUpdateRoute(state);
  }, { flush: 'sync' });

  const login = () => trackName({ name: 'Logged in' });

  /* ===========================
  App Navbar
  =========================== */

  const search = () => trackName({ name: 'performed search using nav searchbar' });

  const navLinkClick = ({ label }) => trackName({ name: `clicked ${label} link in navbar` });

  const navDropdownHover = ({ label }) => trackName({ name: `hovered over ${label} dropdown in navbar` });

  /* ===========================
  Breadcrumbs navigation
  ============================ */
  const breadCrumbLinkClick = ({ label }) => trackName({ name: `Clicked ${label} link in Breadcrumbs` });

  /* ===========================
  Library
  =========================== */

  const librarySidebarClick = ({ linkLabel }) => trackName({
    name: `clicked '${capitalize(linkLabel)}' in Library sidebar`,
  });

  const libraryReqReportLinkClick = ({ req_uuid: reqUUid, id, title }) => track({
    name: 'clicked Req Report link in Post Library',
    params: { ReqId: reqUUid, JobId: id, JobTitle: title },
  });

  /* ===========================
  Filterbar
  =========================== */

  const EMPLOYEE_ACKNOWLEDGEMENT_LIBRARY_FILTER_LABELS = [
    'Employee Name', 'Employee Status', 'Job Family Group',
    'Job Profile', 'HRIS FLSA Status', 'Job Level',
  ];

  const APPROVALS_LIBRARY_FILTER_LABELS = [
    'Status', 'Created By', 'Approvers',
  ];

  const FLSA_SURVEY_LIBRARY_FILTER_LABELS = [
    'Alert Status', 'HRIS FLSA Status', 'FLSA Form Status', 'Job Level',
    'Job Code', 'Primary Exemption Basis', 'Created By',
  ];

  const JOB_PROFILE_LIBRARY_FILTER_LABELS = [
    'Job Family', 'Job Family Group', 'Job Level', 'Job Code', 'Department',
    'Status', 'Primary Language', 'HRIS FLSA Status', 'Location', 'Users',
    'Visibility',
  ];

  const LIBRARY_FILTER_LABELS = [
    ...EMPLOYEE_ACKNOWLEDGEMENT_LIBRARY_FILTER_LABELS,
    ...APPROVALS_LIBRARY_FILTER_LABELS,
    ...FLSA_SURVEY_LIBRARY_FILTER_LABELS,
    ...JOB_PROFILE_LIBRARY_FILTER_LABELS,
  ];

  const filterApply = ({ filters, page }) => {
    filters.forEach((filter) => {
      const filterType = [
        'Department', 'Users', 'Seniority', 'Location', 'Job Family', 'Status', 'Role', 'Country',
        ...LIBRARY_FILTER_LABELS,
      ].includes(filter) || filter.endsWith(' Link') ? filter : 'Custom';
      trackName({ name: `Applied '${filterType}' filters on ${capitalize(page)} page` });
    });
  };

  const filterClear = ({ filter, page }) => {
    const filterType = [
      'Department', 'Users', 'Seniority', 'Location', 'Job Family', 'Status', 'Role', 'all', 'Country',
      ...LIBRARY_FILTER_LABELS,
    ].includes(filter) || filter.endsWith(' Link') ? filter : 'Custom';
    trackName({ name: `Cleared '${filterType}' filters on ${capitalize(page)} page` });
  };

  const filterSubMenuClick = ({ option }) => trackName({ name: `Selected '${option}' in filter submenu` });

  const filterMyJobsClick = ({ enable, page }) => trackName({
    name: `${enable ? 'Selected' : 'Unselected'} 'My Jobs' filter on ${capitalize(page)} page`,
  });

  const filterDateRange = ({ page, dates }) => {
    track({
      name: `Filtered by date on ${capitalize(page)} page`,
      params: {
        To: dates.to,
        From: dates.from,
      },
    });
  };

  /* ===========================
  New Events Filters
  =========================== */
  const appliedFilters = ({ page, filterTypes, filterName = '' }) => {
    filterTypes.forEach((filter) => {
      const filterType = [
        'Department', 'Users', 'Seniority', 'Location', 'Job Family', 'Status', 'Role', 'My Jobs',
        ...LIBRARY_FILTER_LABELS,
      ].includes(filter) ? filter : 'Custom';
      track({
        name: 'Applied Filters',
        params: { $page: page, $filter_type: filterType, $filter_name: filterName },
      });
    });
  };

  /* ===========================
  Analytics
  =========================== */

  const downloadClick = ({ type, page, label = '' }) => track({ name: `Reporting/${capitalize(page)}: Clicked ${type} download`, params: { $label: label } });

  const passthroughCalcUpdate = () => trackName({ name: 'Reporting/Pass-through: Changed Pass-through calc number' });

  const inboundTableToggle = ({ label }) => trackName({ name: `Reporting/Inbound-funnel: Toggled to ${label} chart` });

  const lineChartHover = () => trackName({ name: 'Reporting/Gender-report: Hovered over data in Gender Representation by Stage chart' });

  /* ===========================
  Teams
  =========================== */

  const allMembersSelected = ({ selected }) => {
    const action = selected.value ? '\'Select All\'' : '\'Deselect All\'';
    trackName({ name: `Teams page: ${action} clicked` });
  };

  const singleInviteSent = ({ id }) => track({
    name: 'Teams page: Send single invite button clicked',
    params: { invitedUserId: id },
  });

  const singleDatapeopleAnywhereInviteSent = ({ id }) => track({
    name: 'Teams page: Datapeople Anywhere Invite Clicked',
    params: { invitedUserId: id },
  });

  const massInviteSent = ({ selectedCount }) => track({
    name: 'Teams page: Send mass invite button clicked',
    params: { membersInvited: selectedCount },
  });

  const teamsViewUpdated = (event) => {
    const updateOpt = Object.keys(event).find((key) => [
      'searchTerm', 'selectedSortOption', 'page',
    ].includes(key));

    const type = {
      selectedSortOption: 'sort',
      searchTerm: 'search',
      page: 'pagination',
    }[updateOpt];

    let info;
    if (type === 'search') {
      info = event[updateOpt] === '' ? 'clear search' : event[updateOpt];
    }
    if (type === 'pagination') {
      info = event[updateOpt];
    }
    if (type === 'sort') {
      info = event.descending ? `${event[updateOpt]} descending` : `${event[updateOpt]} ascending`;
      trackName({ name: `Teams page: Update Teams view using '${type}: ${info}'` });
    } else {
      const key = (type === 'search') ? 'searchTerm' : 'page';
      track({
        name: `Teams page: Update Teams view using ${type}`,
        params: { [key]: info },
      });
    }
  };

  /* ===========================
  Editor
  =========================== */

  const editorPreview = ({ job }) => trackJob({ name: 'Viewed: Document Preview', job });

  const editorPage = ({ id }) => track({ name: 'landed on single job page', params: { Id: Number(id) } });

  const editorLoaded = ({ job, props: { duration } }) => trackJob({
    name: 'job page loaded',
    job,
    props: { Duration: duration },
  });

  const editorRescore = ({ job, props: { duration } }) => trackJob({
    name: 'Revision Scored',
    job,
    props: { Duration: duration },
  });

  const editorAction = ({ action }) => trackName({ name: `${action} in editor` });

  /* ===========================
  Editor v5
  ============================ */

  const editorToolbarClick = ({ id }) => {
    const buttonIdToTrackMessage = {
      bold: 'Clicked Format Text: Bold',
      italic: 'Clicked Format Text: Italic',
      link: 'Clicked Format Text: URL',
      clear: 'Clicked Format Text: Remove Formatting',
      undo: 'Clicked Undo',
      redo: 'Clicked Redo',
      indent: 'Clicked Indent List',
      unindent: 'Clicked Unindent List',
      list: 'Clicked Format Text: List',
      paragraph: 'Clicked Format Text: Paragraph',
      heading: 'Clicked Format Text: Header',
      h1: 'Clicked Header Type: H1',
      h2: 'Clicked Header Type: H2',
      h3: 'Clicked Header Type: H3',
      orderedList: 'Clicked Ordered List',
      bulletList: 'Clicked Bullet List',
    };

    const message = buttonIdToTrackMessage[id] || `Clicked toolbar button: ${id}`;
    trackName({ name: message });
  };

  const editorViewReqReportClicked = (reqReportUrl) => track({ name: 'Editor: Clicked View Req Report', params: { reqUrl: reqReportUrl } });

  /* ===========================
  Editor Submenu
  =========================== */

  const editorLinkClick = ({ label }) => trackName({ name: `clicked ${label} link in editor submenu` });

  const editorDropdownHover = ({ label }) => trackName({
    name: `hovered over ${label} dropdown in editor submenu`,
  });

  const hoveredJob = ({ job }) => trackJob({ name: 'HoveredJobReport', job });

  const jobReportClick = ({ job }) => trackJob({ name: 'Clicked on Single Job Report', job });

  /* ===========================
  Apply Template feature
  =========================== */

  const expandedTemplateOptions = () => trackName({ name: 'expanded template options in modal' });

  const applyTemplateModalClick = ({ id, revisionId }) => track({
    name: 'clicked Apply Template button in modal',
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
    },
  });

  const templateApplied = ({ id, revisionId, newRevisionId }) => track({
    name: 'template was applied to job',
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
      NewRevisionId: Number(newRevisionId),
    },
  });

  const revertRevision = ({ id, revisionId }) => track({
    name: 'clicked cancel on template revision page',
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
    },
  });

  const acceptRevision = ({ id, revisionId }) => track({
    name: 'clicked apply on template revision page',
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
    },
  });

  /* ===========================
  Side-By-Side Editor
  =========================== */

  const sideBySidePage = ({ id, revisionId, type }) => track({
    name: `landed on side-by-side ${type} page`,
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
    },
  });

  const sideBySideCopyButtonClicked = ({ id, revisionId, type }) => track({
    name: `clicked copy to clipboard button on side-by-side ${type} page`,
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
    },
  });

  const sideBySideCopyKeyboard = ({
    id, revisionId, revisionColumn, type,
  }) => track({
    name: `used keyboard shortcut to copy to clipboard on side-by-side ${type} page`,
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
      RevisionColumn: revisionColumn,
    },
  });

  const sideBySidePaste = ({
    id, revisionId, revisionColumn, type,
  }) => track({
    name: `pasted content on side-by-side ${type} page`,
    params: {
      Id: Number(id),
      RevisionId: Number(revisionId),
      RevisionColumn: revisionColumn,
    },
  });

  /* ===========================
  Clipboard Tracking
  =========================== */

  const copyAllFields = ({ job }) => trackJob({ name: 'Copy All Clicked', job });

  const copyTitle = ({ job }) => trackJob({ name: 'Copy Title Clicked', job });

  const copyContent = ({ job, props: { field } }) => trackJob({
    name: 'Copy Content Clicked',
    job,
    props: { Field: field },
  });

  /* ===========================
  Revision History
  =========================== */

  const viewJobRevisions = ({ job }) => trackJob({ name: 'Viewed Revision History', job });

  const clickJobRevision = ({ job, props: { revisionId } }) => trackJob({
    name: 'Clicked on historical revision',
    job,
    props: { 'Revision Id': revisionId },
  });

  const viewJobRevisionsInModal = ({ job, props: { revisionId } }) => trackJob({
    name: 'Viewed Revision History in revision modal',
    job,
    props: { 'Revision Id': revisionId },
  });

  const clickJobRevisionInModal = ({
    job, props: { revisionId, newRevision },
  }) => trackJob({
    name: 'Clicked on historical revision in revision modal',
    job,
    props: {
      'Revision Id': revisionId,
      'New Revision Id': newRevision,
    },
  });

  const notify = ({ job, props: { name, type } }) => trackJob({
    name: 'Notify',
    job,
    props: {
      Name: name,
      Type: type,
    },
  });

  const shareJob = ({ job, props: { firstName, lastName, email } }) => trackJob({
    name: 'Job Shared',
    job,
    props: {
      'First Name': firstName,
      'Last Name': lastName,
      Email: email,
    },
  });

  /* ===========================
  Similar Jobs
  =========================== */

  const viewSimilarJobs = ({ job }) => trackJob({ name: 'Viewed Similar Jobs', job });

  const clickSimilarJob = ({ job, props: { id, title, company } }) => trackJob({
    name: 'Clicked on Similar Job',
    job,
    props: {
      'Similar Id': id,
      'Similar Title': title,
      'Similar Company': company,
    },
  });

  /* ===========================
  Sync Job
  =========================== */

  const syncToAts = ({ job, props: { atsName, integrationTypeKey } }) => trackJob({
    name: 'Sync to ATS Button Clicked',
    job,
    props: {
      Integration: integrationTypeKey,
      'Ats Name': atsName,
    },
  });

  const syncModalOpen = ({ job, props: { atsName, integrationTypeKey } }) => trackJob({
    name: 'Sync Modal Opened',
    job,
    props: {
      Integration: integrationTypeKey,
      'Ats Name': atsName,
    },
  });

  const syncError = ({
    job,
    props: {
      code, status, atsName, missing,
    },
  }) => trackJob({
    name: 'Sync Error',
    job,
    props: {
      Code: code,
      Status: status,
      'Ats Name': atsName,
      'Missing Article': missing,
    },
  });

  /* ===========================
  Highlights
  =========================== */

  const highlightActionTitle = {
    replace: 'Replace',
    delete: 'Delete',
    warn: 'Warn',
    replace_or_delete: 'Replace or delete',
    insert_before_hl: 'Insert',
    insert_after_hl: 'Insert',
  };

  const viewHighlight = ({ job, props }) => {
    const {
      action, issue, ruleId, description, tip, suggestions,
    } = props;

    trackJob({
      name: 'Viewed Actionable Highlight',
      job,
      props: {
        'Action Type': highlightActionTitle[action] || action,
        Issue: issue,
        'Rule ID': ruleId,
        Description: description,
        Tip: tip,
        Suggestions: JSON.stringify(suggestions),
      },
    });
  };

  const applyHighlight = ({
    job, props: {
      action, replacement, removedText, color, suggestions,
    },
  }) => trackJob({
    name: 'Applied Actionable Highlight',
    job,
    props: {
      'Action Type': highlightActionTitle[action] || action,
      'Inserted Text': replacement,
      'Removed Text': removedText,
      Color: color,
      Suggestions: JSON.stringify(suggestions),
    },
  });

  const viewNudge = ({ job, props }) => trackJob({
    name: 'Viewed Nudge', job, props,
  });

  const applyNudge = ({ job, props }) => trackJob({
    name: 'Applied Nudge', job, props,
  });

  const readMoreLinkClicked = ({
    job, props: {
      link, action, replacement, removedText,
    },
  }) => trackJob({
    name: 'Read More Link Clicked',
    job,
    props: {
      Link: link,
      'Action Type': highlightActionTitle[action] || action,
      'Inserted Text': replacement,
      'Removed Text': removedText,
    },
  });

  /* ===========================
  Insights
  =========================== */

  const insightsTrackWidgetLinkClick = (linkName, routeName) => trackName({ name: `Insights/Clicked ${linkName} link in ${routeName} widget` });

  const insightsShareReportViaEmail = ({
    reportName, sharedUserStatus, noteIncluded,
  }) => track({ name: 'Emailed Insights Report Link', params: { reportName, sharedUserStatus, noteIncluded } });

  const actionableInsightsShareJobsViaEmail = ({
    reportName, sharedUserStatus, noteIncluded,
  }) => track({ name: 'Shared Selected Actionable Jobs', params: { reportName, sharedUserStatus, noteIncluded } });

  const insightsShareReportImage = ({
    reportName, sharedUserStatus, noteIncluded,
  }) => track({ name: 'Emailed Insights Report Image', params: { reportName, sharedUserStatus, noteIncluded } });

  const insightsCopiedReportLink = ({
    reportName,
  }) => track({ name: 'Copied Insights Report Link', params: { reportName } });

  const insightsDownloadReport = ({
    reportName, downloadType, downloadLocation, fileType, widgetName,
  }) => track({
    name: 'Downloaded Insights Report',
    params: {
      reportName, downloadType, downloadLocation, fileType, widgetName,
    },
  });

  /* ===========================
  Requisitions
  =========================== */

  const requisitionsButtonClick = ({ type, page }) => trackName({ name: `Requisitions/${capitalize(page)}: Clicked ${type} button` });

  const requisitionOpenReqReport = (reqUuid) => track({ name: 'Requisition/Library: Opened Requisition Report', params: { Id: reqUuid } });

  const requisitionReportLanded = (reqUuid) => track({ name: 'Landed on Requisition Report', params: { Id: reqUuid } });

  const requisitionJobPostClicked = (jobId) => track({ name: 'Requisition Report: Clicked Job Post', params: { Id: jobId } });

  /* ===========================
  Home
  =========================== */

  const homeLogoClicked = () => trackName({ name: 'Clicked Homepage (Logo)' });

  const homeClick = (label) => trackName({ name: `Home/Clicked ${label}` });

  const homeHover = (label) => trackName({ name: `Home/Hovered over ${label}` });

  const homeLearnHover = ({ label, url }) => track({ name: `Home/Hovered over Learn article/video: ${label}`, params: { Url: url } });

  const homeLearnClick = ({ label, url }) => track({ name: `Home/Clicked Learn article/video: ${label}`, params: { Url: url } });

  /* ===========================
  Demographic Survey Report
  =========================== */
  const dsrClickedViewData = ({ tab, modal }) => track({ name: 'Clicked “View Data” in Modal in Demographic-survey-report page', params: { navigatingToTab: tab, modal } });

  const dsrClickedViewGenderReport = ({ modal }) => trackName({ name: 'Clicked “View Gender Report” in Modal in Demographic-survey-report page', params: { modal } });

  /* ===========================
  Flagship Jobs
  =========================== */

  const flagshipFlaggedAsFlagship = () => trackName({ name: 'Flagship Post: Flagged as Flagship' });

  const flagshipRemoveFlagship = () => trackName({ name: 'Flagship Post: Removed Flagship' });

  const flagshipVisibility = ({ origin, isHidden }) => track({ name: 'Flagship Post: Flagship Visibility', params: { IsHidden: isHidden, Origin: origin } });

  const flagshipDuplicate = ({ origin }) => track({ name: 'Flagship Post: Duplicate from Flagship', params: { Origin: origin } });

  const draftPostStatusToggle = ({ status }) => track({ name: 'Draft Post: Toggled Status', params: { Status: status } });

  const templateHideShowTemplate = ({ isHidden, origin }) => track({ name: 'Job Template: Template Visibility', params: { IsHidden: isHidden, Origin: origin } });

  /* ===========================
  Acknowledgement Library & Settings
  =========================== */

  const ACK_PAGE_LIBRARY = 'Acknowledgement Library';
  const ACK_PAGE_SETTINGS = 'Acknowledgement Settings';

  const ackLibSelectedMetricCard = ({ status }) => track({
    name: 'Selected Metric Card',
    params: { Page: ACK_PAGE_LIBRARY, Status: status },
  });

  const ackLibAppliedTextSearch = () => track({
    name: 'Applied Text box filter',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibSortedTable = ({ column, direction }) => track({
    name: 'Sorted Table',
    params: {
      Page: ACK_PAGE_LIBRARY, Column: column, Direction: direction,
    },
  });

  const ackLibClickedCreate = ({ location }) => track({
    name: 'Clicked Create Acknowledgement',
    params: { Page: ACK_PAGE_LIBRARY, Location: location },
  });

  const ackLibClickedRemind = ({ location }) => track({
    name: 'Clicked Remind',
    params: { Page: ACK_PAGE_LIBRARY, Location: location },
  });

  const ackLibClickedCancel = ({ location }) => track({
    name: 'Clicked Cancel Acknowledgement',
    params: { Page: ACK_PAGE_LIBRARY, Location: location },
  });

  const ackLibDownloadedCsv = () => track({
    name: 'Downloaded CSV of Employee Table',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibDownloadedPdf = ({ type }) => track({
    name: 'Downloaded Acknowledgement from Sidebar',
    params: { Page: ACK_PAGE_LIBRARY, Type: type },
  });

  const ackLibClickedViewJobProfile = () => track({
    name: 'Clicked View Job Profile link in Table',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibClickedLearnMore = () => track({
    name: 'Clicked Learn More in Preamble',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackSettingsUpdated = ({ section, field }) => track({
    name: 'Updated Acknowledgement Settings',
    params: { Page: ACK_PAGE_SETTINGS, Section: section, Field: field },
  });

  const ackSettingsReverted = ({ section, field }) => track({
    name: 'Reverted Acknowledgement Settings to Default',
    params: { Page: ACK_PAGE_SETTINGS, Section: section, Field: field },
  });

  const ackLibOpenedEmployeePreview = () => track({
    name: 'Opened Employee Preview',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibClickedCompareJobProfiles = () => track({
    name: 'Clicked Compare Job Profiles',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibOpenedCompareModal = () => track({
    name: 'Opened Compare Job Profiles Modal',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  const ackLibOpenedAcknowledgementPreview = () => track({
    name: 'Opened Acknowledgement Preview',
    params: { Page: ACK_PAGE_LIBRARY },
  });

  /* ===========================
  Approvals Dashboard & Flows
  =========================== */

  const APPROVAL_PAGE_DASHBOARD = 'Approvals Dashboard';
  const APPROVAL_PAGE_ACTIONS = 'Approval Actions';
  const APPROVAL_PAGE_TEMPLATE_EDITOR = 'Approval Template Editor';
  const APPROVAL_PAGE_PROFILE_PANEL = 'Job Profile Approvals Panel';

  const approvalLibSelectedMetricCard = ({ status }) => track({
    name: 'Selected Metric Card',
    params: { Page: APPROVAL_PAGE_DASHBOARD, Status: status },
  });

  const approvalLibAppliedTextSearch = () => track({
    name: 'Applied Text box filter',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibSortedTable = ({ column, direction }) => track({
    name: 'Sorted Table',
    params: {
      Page: APPROVAL_PAGE_DASHBOARD, Column: column, Direction: direction,
    },
  });

  const approvalLibOpenedPreview = () => track({
    name: 'Opened Approval Flow Preview',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibClickedRemind = () => track({
    name: 'Clicked Remind',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibClickedCancel = () => track({
    name: 'Clicked Cancel Approval Flow',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibClickedViewJobProfile = () => track({
    name: 'Clicked View Job Profile link',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibClickedLearnMore = () => track({
    name: 'Clicked Learn More in Preamble',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibDownloadedCsv = () => track({
    name: 'Downloaded CSV',
    params: { Page: APPROVAL_PAGE_DASHBOARD },
  });

  const approvalLibRemindedApprover = () => track({
    name: 'Reminded Approver',
    params: { Page: APPROVAL_PAGE_DASHBOARD, Location: 'Slot Menu' },
  });

  const approvalPanelStartedFlow = () => track({
    name: 'Started Approval Flow',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL },
  });

  const approvalPanelClickedStartFlow = () => track({
    name: 'Clicked Start Approval Flow',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL },
  });

  const approvalPanelViewedFlowDetail = ({ status }) => track({
    name: 'Viewed Approval Flow Detail',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL, Status: status },
  });

  const approvalPanelOpenedEditFlow = () => track({
    name: 'Opened Edit Approval Flow',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL },
  });

  const approvalPanelSavedFlowChanges = () => track({
    name: 'Saved Approval Flow Changes',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL },
  });

  const approvalPanelCancelledFlow = ({ location }) => track({
    name: 'Cancelled Approval Flow',
    params: { Page: APPROVAL_PAGE_PROFILE_PANEL, Location: location },
  });

  const approvalActionsApproved = () => track({
    name: 'Approved Job Profile',
    params: { Page: APPROVAL_PAGE_ACTIONS },
  });

  const approvalActionsRejected = () => track({
    name: 'Rejected Job Profile',
    params: { Page: APPROVAL_PAGE_ACTIONS },
  });

  const approvalTemplateCreated = () => track({
    name: 'Created Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateSaved = () => track({
    name: 'Saved Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateDuplicated = () => track({
    name: 'Duplicated Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateArchived = () => track({
    name: 'Archived Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateUnarchived = () => track({
    name: 'Unarchived Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateDeleted = () => track({
    name: 'Deleted Approval Template',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateAddedStage = () => track({
    name: 'Added Approval Stage',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalTemplateRemovedStage = () => track({
    name: 'Removed Approval Stage',
    params: { Page: APPROVAL_PAGE_TEMPLATE_EDITOR },
  });

  const approvalHomeClickedView = () => track({
    name: 'Clicked View Approval from Home',
    params: { Page: 'Home' },
  });

  /* ===========================
  FLSA Surveys
  =========================== */

  const FLSA_PAGE_DASHBOARD = 'FLSA Dashboard';
  const FLSA_PAGE_SETTINGS = 'FLSA Settings';
  const FLSA_PAGE_FORM = 'FLSA Form';
  const FLSA_PAGE_PROFILE_PANEL = 'Job Profile FLSA Panel';

  const flsaLibSelectedMetricCard = ({ status }) => track({
    name: 'Selected Metric Card',
    params: { Page: FLSA_PAGE_DASHBOARD, Status: status },
  });

  const flsaLibAppliedTextSearch = () => track({
    name: 'Applied Text box filter',
    params: { Page: FLSA_PAGE_DASHBOARD },
  });

  const flsaLibSortedTable = ({ column, direction }) => track({
    name: 'Sorted Table',
    params: {
      Page: FLSA_PAGE_DASHBOARD, Column: column, Direction: direction,
    },
  });

  const flsaLibToggledViewMode = ({ mode }) => track({
    name: 'Toggled HRIS/Draft View',
    params: { Page: FLSA_PAGE_DASHBOARD, Mode: mode },
  });

  const flsaLibOpenedPreview = () => track({
    name: 'Opened FLSA Profile Preview',
    params: { Page: FLSA_PAGE_DASHBOARD },
  });

  const flsaLibOpenedSubmission = () => track({
    name: 'Opened FLSA Submission',
    params: { Page: FLSA_PAGE_DASHBOARD },
  });

  const flsaLibClickedCreateNew = ({ jurisdiction }) => track({
    name: 'Clicked Create New FLSA Survey',
    params: {
      Page: FLSA_PAGE_DASHBOARD, Jurisdiction: jurisdiction,
    },
  });

  const flsaLibDownloadedCsv = () => track({
    name: 'Downloaded CSV',
    params: { Page: FLSA_PAGE_DASHBOARD },
  });

  const flsaLibClickedLearnMore = ({ page = FLSA_PAGE_DASHBOARD } = {}) => track({
    name: 'Clicked Learn More in Preamble',
    params: { Page: page },
  });

  const flsaLibClickedViewJobProfile = () => track({
    name: 'Clicked View Job Profile link',
    params: { Page: FLSA_PAGE_DASHBOARD },
  });

  const flsaSettingsUpdated = ({ section, field }) => track({
    name: 'Updated FLSA Settings',
    params: { Page: FLSA_PAGE_SETTINGS, Section: section, Field: field },
  });

  const flsaSettingsReverted = ({ section, field }) => track({
    name: 'Reverted FLSA Settings to Default',
    params: { Page: FLSA_PAGE_SETTINGS, Section: section, Field: field },
  });

  const flsaPanelStartedQuestionnaire = ({ jurisdiction }) => track({
    name: 'Started FLSA Questionnaire',
    params: { Page: FLSA_PAGE_PROFILE_PANEL, Jurisdiction: jurisdiction },
  });

  const flsaFormNavigatedSection = ({ section }) => track({
    name: 'Navigated FLSA Section',
    params: { Page: FLSA_PAGE_FORM, Section: section },
  });

  const flsaFormSubmitted = ({ jurisdiction }) => track({
    name: 'Submitted FLSA Form',
    params: { Page: FLSA_PAGE_FORM, Jurisdiction: jurisdiction },
  });

  /* ===========================
  Job Architecture
  =========================== */

  const JA_PAGE_LIST = 'Job Architecture List View';
  const JA_PAGE_MATRIX = 'Job Architecture Matrix View';

  const jaListClickedLearnMore = () => track({
    name: 'Clicked Learn More in List View Preamble',
    params: { Page: JA_PAGE_LIST },
  });

  const jaListDownloadedCsv = () => track({
    name: 'Downloaded CSV (List View)',
    params: { Page: JA_PAGE_LIST },
  });

  const jaListClickedCompare = () => track({
    name: 'Clicked Compare Job Profiles',
    params: { Page: JA_PAGE_LIST },
  });

  const jaListAppliedSidebarFilter = ({ filterName }) => track({
    name: 'Applied Sidebar Filter',
    params: { Page: JA_PAGE_LIST, 'Filter Name': filterName },
  });

  const jaListAppliedTextSearch = () => track({
    name: 'Applied Text Box Filter',
    params: { Page: JA_PAGE_LIST },
  });

  const jaListOpenedJobProfileFromSidebar = () => track({
    name: 'Opened Job Profile from Sidebar Preview',
    params: { Page: JA_PAGE_LIST },
  });

  const jaListExpandedAlertInfoBox = () => track({
    name: 'Expanded Alert Info Box in Sidebar Preview',
    params: { Page: JA_PAGE_LIST },
  });

  const jaMatrixClickedLearnMore = () => track({
    name: 'Clicked Learn More in Matrix Preamble',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixDownloadedCsv = () => track({
    name: 'Downloaded CSV (Matrix View)',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixOpenedCommentSidebar = () => track({
    name: 'Opened Comment Sidebar',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixAddedComment = ({ section = '' }) => track({
    name: 'Added Comment',
    params: { Page: JA_PAGE_MATRIX, Section: section },
  });

  const jaMatrixHoveredAlertIcon = () => track({
    name: 'Hovered Alert Icon in Column Header',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixAddedJobProfile = () => track({
    name: 'Added Job Profile',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixAddedJobProfileField = () => track({
    name: 'Added Job Profile Field',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixRemovedJobProfile = () => track({
    name: 'Removed Job Profile',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixRemovedJobProfileField = () => track({
    name: 'Removed Job Profile Field',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixReorderedColumn = () => track({
    name: 'Reordered Column',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixReorderedRow = () => track({
    name: 'Reordered Row',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixOpenedJobProfileFromHeader = () => track({
    name: 'Opened Job Profile from Column Header',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaListSelectedProfiles = ({ count }) => track({
    name: 'Selected Job Profiles for Compare',
    params: { Page: JA_PAGE_LIST, Count: count },
  });

  const jaListOpenedJobProfilePreview = () => track({
    name: 'Opened Job Profile Preview',
    params: { Page: JA_PAGE_LIST },
  });

  const jaMatrixToggledColumnVisibility = () => track({
    name: 'Toggled Column Visibility',
    params: { Page: JA_PAGE_MATRIX },
  });

  const jaMatrixToggledRowVisibility = () => track({
    name: 'Toggled Row Visibility',
    params: { Page: JA_PAGE_MATRIX },
  });

  /* ===========================
  Job Profile Libraries
  =========================== */

  const jpLibAppliedTextSearch = ({ page }) => track({
    name: 'Applied Text box filter',
    params: { Page: page },
  });

  const jpLibSortedTable = ({ page, column, direction }) => track({
    name: 'Sorted Table',
    params: { Page: page, Column: column, Direction: direction },
  });

  const jpLibOpenedPreview = ({ page, profileType }) => track({
    name: 'Opened Job Profile Preview',
    params: { Page: page, 'Profile Type': profileType },
  });

  const jpLibOpenedInEditor = ({ page, profileType }) => track({
    name: 'Opened Job Profile in Editor',
    params: { Page: page, 'Profile Type': profileType },
  });

  const jpLibClickedCreateTemplate = ({ page }) => track({
    name: 'Clicked Create Template',
    params: { Page: page },
  });

  const jpLibCreatedTemplate = ({ page }) => track({
    name: 'Created Job Profile Template',
    params: { Page: page },
  });

  const jpLibClickedEditDraft = ({ page }) => track({
    name: 'Clicked Edit Draft',
    params: { Page: page },
  });

  const jpLibClickedDeleteDraft = ({ page }) => track({
    name: 'Clicked Delete Draft',
    params: { Page: page },
  });

  const jpLibDeletedDraft = ({ page }) => track({
    name: 'Deleted Draft Job Profile',
    params: { Page: page },
  });

  const jpLibClickedCompare = ({ page, count }) => track({
    name: 'Clicked Compare Selected',
    params: { Page: page, Count: count },
  });

  const jpLibClickedClearSelection = ({ page }) => track({
    name: 'Clicked Clear Selection',
    params: { Page: page },
  });

  const jpLibClickedLearnMore = ({ page }) => track({
    name: 'Clicked Learn More',
    params: { Page: page },
  });

  /* ===========================
  Job Profile Settings
  =========================== */

  const JOB_PROFILE_PAGE_SETTINGS = 'Job Profile Settings';

  const jobProfileSettingsUpdated = ({ section, field }) => track({
    name: 'Updated Job Profile Settings',
    params: { Page: JOB_PROFILE_PAGE_SETTINGS, Section: section, Field: field },
  });

  /* ===========================
  Job Profile Editor
  =========================== */

  const JP_PAGE_EDITOR = 'Job Profile Editor';

  const jpEditorPage = ({ ulid, profileType }) => track({
    name: 'landed on single job profile page',
    params: { Ulid: ulid, 'Profile Type': profileType },
  });

  const jpEditorLoaded = ({
    ulid, profileType, title, duration,
  }) => track({
    name: 'job profile page loaded',
    params: {
      Ulid: ulid,
      'Profile Type': profileType,
      'Job Title': title,
      Duration: duration,
    },
  });

  const jpEditorRescore = ({ ulid, revisionUlid, duration }) => track({
    name: 'Job Profile Revision Scored',
    params: {
      Ulid: ulid,
      RevisionUlid: revisionUlid,
      Duration: duration,
    },
  });

  const jpEditorApplyTemplateClick = ({ ulid, templateUlid }) => track({
    name: 'clicked Apply Template button in modal',
    params: { Ulid: ulid, TemplateUlid: templateUlid },
  });

  const jpEditorTemplateApplied = ({ ulid, templateUlid, newRevisionUlid }) => track({
    name: 'template was applied to job profile',
    params: {
      Ulid: ulid,
      TemplateUlid: templateUlid,
      NewRevisionUlid: newRevisionUlid,
    },
  });

  const jpEditorPublish = ({ ulid, profileType }) => track({
    name: 'Published Job Profile Template',
    params: { Ulid: ulid, 'Profile Type': profileType },
  });

  const jpEditorDelete = ({ ulid, profileType }) => track({
    name: 'Deleted Job Profile',
    params: { Ulid: ulid, 'Profile Type': profileType },
  });

  const jpEditorCreateDraftFromHris = ({ ulid, newUlid }) => track({
    name: 'Created Draft from HRIS Job Profile',
    params: { Ulid: ulid, NewUlid: newUlid },
  });

  const jpEditorOpenedTab = ({ tab }) => track({
    name: 'Opened Job Profile Editor Tab',
    params: { Page: JP_PAGE_EDITOR, Tab: tab },
  });

  const jpEditorSelectedTitleAndLocation = ({ title, location, jobCode = '' }) => track({
    name: 'Selected Title and Location for New Job Profile',
    params: {
      Page: JP_PAGE_EDITOR,
      Title: title,
      Location: location,
      'Job Code': jobCode,
    },
  });

  const jpEditorAddedComment = () => track({
    name: 'Added Comment',
    params: { Page: JP_PAGE_EDITOR },
  });

  const jpEditorOpenedCompare = ({ ulid, revisionUlid }) => track({
    name: 'Opened Job Profile Revision Compare',
    params: { Ulid: ulid, RevisionUlid: revisionUlid },
  });

  const jpEditorRestoredRevision = ({ ulid, revisionUlid }) => track({
    name: 'Restored Job Profile Revision',
    params: { Ulid: ulid, RevisionUlid: revisionUlid },
  });

  return {
    // actions
    login,
    trackPageLoad,
    trackName,
    track,
    editorAction,
    editorDropdownHover,
    editorLinkClick,
    editorLoaded,
    editorPage,
    editorPreview,
    editorRescore,
    editorToolbarClick,
    editorViewReqReportClicked,
    hoveredJob,
    jobReportClick,
    applyHighlight,
    viewHighlight,
    viewNudge,
    applyNudge,
    syncModalOpen,
    syncToAts,
    syncError,
    shareJob,
    notify,
    clickSimilarJob,
    viewSimilarJobs,
    clickJobRevision,
    clickJobRevisionInModal,
    viewJobRevisions,
    viewJobRevisionsInModal,
    copyAllFields,
    copyContent,
    copyTitle,
    sideBySideCopyButtonClicked,
    sideBySideCopyKeyboard,
    sideBySidePage,
    sideBySidePaste,
    acceptRevision,
    applyTemplateModalClick,
    templateApplied,
    expandedTemplateOptions,
    revertRevision,
    search,
    navLinkClick,
    navDropdownHover,
    breadCrumbLinkClick,
    allMembersSelected,
    singleInviteSent,
    singleDatapeopleAnywhereInviteSent,
    massInviteSent,
    teamsViewUpdated,
    downloadClick,
    passthroughCalcUpdate,
    inboundTableToggle,
    lineChartHover,
    filterApply,
    filterClear,
    filterSubMenuClick,
    filterMyJobsClick,
    filterDateRange,
    librarySidebarClick,
    libraryReqReportLinkClick,
    readMoreLinkClicked,
    insightsTrackWidgetLinkClick,
    insightsShareReportViaEmail,
    actionableInsightsShareJobsViaEmail,
    insightsShareReportImage,
    insightsCopiedReportLink,
    insightsDownloadReport,
    requisitionsButtonClick,
    requisitionOpenReqReport,
    requisitionReportLanded,
    requisitionJobPostClicked,
    homeLogoClicked,
    homeClick,
    homeHover,
    homeLearnHover,
    homeLearnClick,
    dsrClickedViewData,
    dsrClickedViewGenderReport,
    appliedFilters,
    flagshipFlaggedAsFlagship,
    flagshipRemoveFlagship,
    flagshipVisibility,
    flagshipDuplicate,
    draftPostStatusToggle,
    templateHideShowTemplate,
    ackLibSelectedMetricCard,
    ackLibAppliedTextSearch,
    ackLibSortedTable,
    ackLibClickedCreate,
    ackLibClickedRemind,
    ackLibClickedCancel,
    ackLibDownloadedCsv,
    ackLibDownloadedPdf,
    ackLibClickedViewJobProfile,
    ackLibClickedLearnMore,
    ackSettingsUpdated,
    ackSettingsReverted,
    ackLibOpenedEmployeePreview,
    ackLibClickedCompareJobProfiles,
    ackLibOpenedCompareModal,
    ackLibOpenedAcknowledgementPreview,
    approvalLibSelectedMetricCard,
    approvalLibAppliedTextSearch,
    approvalLibSortedTable,
    approvalLibOpenedPreview,
    approvalLibClickedRemind,
    approvalLibClickedCancel,
    approvalLibClickedViewJobProfile,
    approvalLibClickedLearnMore,
    approvalLibDownloadedCsv,
    approvalLibRemindedApprover,
    approvalPanelStartedFlow,
    approvalPanelClickedStartFlow,
    approvalPanelViewedFlowDetail,
    approvalPanelOpenedEditFlow,
    approvalPanelSavedFlowChanges,
    approvalPanelCancelledFlow,
    approvalActionsApproved,
    approvalActionsRejected,
    approvalTemplateCreated,
    approvalTemplateSaved,
    approvalTemplateDuplicated,
    approvalTemplateArchived,
    approvalTemplateUnarchived,
    approvalTemplateDeleted,
    approvalTemplateAddedStage,
    approvalTemplateRemovedStage,
    approvalHomeClickedView,
    flsaLibSelectedMetricCard,
    flsaLibAppliedTextSearch,
    flsaLibSortedTable,
    flsaLibToggledViewMode,
    flsaLibOpenedPreview,
    flsaLibOpenedSubmission,
    flsaLibClickedCreateNew,
    flsaLibDownloadedCsv,
    flsaLibClickedLearnMore,
    flsaLibClickedViewJobProfile,
    flsaSettingsUpdated,
    flsaSettingsReverted,
    flsaPanelStartedQuestionnaire,
    flsaFormNavigatedSection,
    flsaFormSubmitted,
    jaListClickedLearnMore,
    jaListDownloadedCsv,
    jaListClickedCompare,
    jaListAppliedSidebarFilter,
    jaListAppliedTextSearch,
    jaListOpenedJobProfileFromSidebar,
    jaListExpandedAlertInfoBox,
    jaMatrixClickedLearnMore,
    jaMatrixDownloadedCsv,
    jaMatrixOpenedCommentSidebar,
    jaMatrixAddedComment,
    jaMatrixHoveredAlertIcon,
    jaMatrixAddedJobProfile,
    jaMatrixAddedJobProfileField,
    jaMatrixRemovedJobProfile,
    jaMatrixRemovedJobProfileField,
    jaMatrixReorderedColumn,
    jaMatrixReorderedRow,
    jaMatrixOpenedJobProfileFromHeader,
    jaListSelectedProfiles,
    jaListOpenedJobProfilePreview,
    jaMatrixToggledColumnVisibility,
    jaMatrixToggledRowVisibility,
    jpLibAppliedTextSearch,
    jpLibSortedTable,
    jpLibOpenedPreview,
    jpLibOpenedInEditor,
    jpLibClickedCreateTemplate,
    jpLibCreatedTemplate,
    jpLibClickedEditDraft,
    jpLibClickedDeleteDraft,
    jpLibDeletedDraft,
    jpLibClickedCompare,
    jpLibClickedClearSelection,
    jpLibClickedLearnMore,
    jobProfileSettingsUpdated,
    jpEditorPage,
    jpEditorLoaded,
    jpEditorRescore,
    jpEditorApplyTemplateClick,
    jpEditorTemplateApplied,
    jpEditorPublish,
    jpEditorDelete,
    jpEditorCreateDraftFromHris,
    jpEditorOpenedTab,
    jpEditorSelectedTitleAndLocation,
    jpEditorAddedComment,
    jpEditorOpenedCompare,
    jpEditorRestoredRevision,
  };
});
